import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { DASHBOARD } from '../../../../../constants/urls';
import { trackConditionReportSectionDone } from '../../../../../services/analytics/marketplaceEvents';
import { useMarketplaceExternalId } from '../../../../../services/marketplace/externalId';
import {
  getLeadByExternalIdQueryKey,
  useGetLeadByExternalId,
} from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import {
  getLeadByExternalIdConditionReportSchemaQueryKey,
  useGetLeadByExternalIdConditionReportSchema,
} from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdConditionReportSchema';
import { usePatchLeadByExternalIdData } from '../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import {
  SchemaStep,
  buildInitialValues,
  buildValidationSchema,
  decodeAnswers,
  encodeAnswers,
  firstIncompleteStepIndex,
} from './encodeDecodeAnswers';

type FormikBag = Pick<FormikHelpers<Record<string, unknown>>, 'setTouched' | 'validateForm'>;

const useMarketplaceConditionReport = (dealId: string) => {
  const externalId = useMarketplaceExternalId(dealId);
  const history = useHistory();
  const toast = useToast();

  const reportQuery = useGetLeadByExternalIdConditionReportSchema({ externalId });
  const leadQuery = useGetLeadByExternalId({ externalId });
  const queryClient = useQueryClient();
  const patchMutation = usePatchLeadByExternalIdData({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getLeadByExternalIdConditionReportSchemaQueryKey({ externalId }),
        });
        queryClient.invalidateQueries({
          queryKey: getLeadByExternalIdQueryKey({ externalId }),
        });
      },
    },
  });

  const report = reportQuery.data;
  const make = leadQuery.data?.make;
  const steps: SchemaStep[] = useMemo(() => {
    const rawSteps = report?.steps ?? [];
    if (!make) return rawSteps;
    return rawSteps.map((step) => ({
      ...step,
      title: step.title.replace(/\{make\}/g, make),
      subtitle: step.subtitle.replace(/\{make\}/g, make),
    }));
  }, [report, make]);
  const validationSchema = useMemo(
    () => (steps.length ? buildValidationSchema(steps) : undefined),
    [steps],
  );

  const [hydratedForExternalId, setHydratedForExternalId] = useState('');
  const [initialValues, setInitialValues] = useState<Record<string, unknown>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const hydrated = !!externalId && hydratedForExternalId === externalId;

  useEffect(() => {
    if (hydrated || !report) return;
    const empty = buildInitialValues(steps);
    const decoded = decodeAnswers(steps);
    const merged = { ...empty, ...decoded };
    setInitialValues(merged);
    setCurrentStep(firstIncompleteStepIndex(steps, merged));
    setHydratedForExternalId(externalId);
  }, [hydrated, report, steps, externalId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const persistStep = useCallback(
    async (values: Record<string, unknown>, step: SchemaStep) => {
      await patchMutation.mutateAsync({
        externalId,
        data: { conditionReport: encodeAnswers(values, step.questions) },
      });
    },
    [patchMutation, externalId],
  );

  const showSaveError = useCallback(() => {
    toast({
      title: 'Could not save',
      description: 'Please try again.',
      status: 'error',
      duration: 4000,
      isClosable: true,
      position: 'top',
    });
  }, [toast]);

  const saveStep = useCallback(
    async (
      values: Record<string, unknown>,
      step: SchemaStep | undefined,
      formik: FormikBag,
    ): Promise<boolean> => {
      if (!step) return false;

      const touched: Record<string, boolean> = {};
      step.questions.forEach((q) => {
        touched[q.questionId] = true;
      });
      await formik.setTouched(touched, true);

      const errors = await formik.validateForm();
      const hasErrors = step.questions.some((q) => errors[q.questionId]);
      if (hasErrors) return false;

      try {
        await persistStep(values, step);
        trackConditionReportSectionDone(dealId, step.title);
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [persistStep, dealId, showSaveError],
  );

  const handleNext = useCallback(
    async (values: Record<string, unknown>, step: SchemaStep | undefined, formik: FormikBag) => {
      const ok = await saveStep(values, step, formik);
      if (ok) setCurrentStep((s) => Math.min(s + 1, steps.length));
    },
    [saveStep, steps.length],
  );

  const handleBack = useCallback(() => setCurrentStep((s) => Math.max(0, s - 1)), []);

  const handleEditAnswers = useCallback(() => setCurrentStep(0), []);

  const handleBackToDashboard = useCallback(() => {
    history.push(DASHBOARD);
  }, [history]);

  return {
    isReady: hydrated,
    loadError: reportQuery.error ?? null,
    steps,
    initialValues,
    validationSchema,
    currentStep,
    isOnDone: hydrated && currentStep >= steps.length,
    isSaving: patchMutation.isPending,
    saveStep,
    handleNext,
    handleBack,
    handleEditAnswers,
    handleBackToDashboard,
  };
};

export default useMarketplaceConditionReport;
